// AdminImportPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/DBContext';

const AdminImportPage = () => {
  const { importServicesFromJson, fetchServices, manualUpdateItemsJson, getServiceImportStats } = useAuth();
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [updatingJson, setUpdatingJson] = useState(false);
  const [serviceStats, setServiceStats] = useState(null);

  // Load service statistics on component mount
  useEffect(() => {
    loadServiceStats();
  }, []);

  const loadServiceStats = async () => {
    try {
      const stats = await getServiceImportStats();
      setServiceStats(stats);
    } catch (error) {
      console.error('Error loading service stats:', error);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setImportStatus('Checking existing services...');
    setImportResult(null);

    try {
      const existingServices = await fetchServices();
      console.log(`Found ${existingServices.length} existing services in database`);
      
      setImportStatus('Loading services from JSON file...');
      
      // Fetch the JSON file - adjust the path based on your file location
      const response = await fetch('/sanitized_services.json');
      if (!response.ok) {
        throw new Error(`Failed to load JSON file: ${response.status} ${response.statusText}`);
      }
      
      const jsonData = await response.json();
      console.log(`Loaded ${jsonData.length} services from JSON file`);
      
      setImportStatus(`Importing services... This may take several minutes for large datasets.`);
      
      const result = await importServicesFromJson(jsonData);
      
      setImportResult({
        success: true,
        imported: result.imported,
        total: result.total,
        existingCount: existingServices.length
      });
      
      setImportStatus(`Import completed! Imported ${result.imported} new services.`);
      
      // Reload statistics after import
      await loadServiceStats();
      
    } catch (error) {
      console.error('Error during service import:', error);
      setImportStatus(`Error: ${error.message}`);
      setImportResult({
        success: false,
        error: error.message
      });
    } finally {
      setImporting(false);
    }
  };

  const handleUpdateItemsJson = async () => {
    setUpdatingJson(true);
    try {
      const result = await manualUpdateItemsJson();
      if (result.success) {
        alert('items.json updated successfully!');
      } else {
        alert(`Error updating items.json: ${result.error}`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setUpdatingJson(false);
    }
  };

  const handleExportToJSON = async () => {
    try {
      setImportStatus('Fetching all services from database...');
      
      // Fetch all services
      const services = await fetchServices();
      
      setImportStatus(`Preparing JSON file with ${services.length} services...`);
      
      // Create JSON content
      const jsonContent = JSON.stringify(services, null, 2);
      
      // Create a blob from the JSON string
      const blob = new Blob([jsonContent], { type: 'application/json' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `services_export_${new Date().toISOString().split('T')[0]}.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      URL.revokeObjectURL(url);
      
      setImportStatus(`Successfully exported ${services.length} services to JSON file`);
      
    } catch (error) {
      console.error('Error exporting services:', error);
      setImportStatus(`Error exporting services: ${error.message}`);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Admin Service Import</h1>
      
      {serviceStats && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '4px' 
        }}>
          <h3>Current Database Statistics</h3>
          <ul>
            <li>Total Services: {serviceStats.totalServices}</li>
            <li>Imported Services: {serviceStats.importedServices}</li>
            <li>Claimed Services: {serviceStats.claimedServices}</li>
            <li>Unclaimed Services: {serviceStats.unclaimedServices}</li>
          </ul>
        </div>
      )}
      
      <div style={{ marginBottom: '20px' }}>
        <p>This tool imports services from a JSON file into the Firestore database.</p>
        <p>The import process:</p>
        <ul>
          <li>Checks for duplicate services (by name)</li>
          <li>Imports in batches of 100 with delays to avoid rate limiting</li>
          <li>Skips items.json update for large imports</li>
        </ul>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button 
          onClick={handleImport} 
          disabled={importing || updatingJson}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: importing ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: importing ? 'not-allowed' : 'pointer'
          }}
        >
          {importing ? 'Importing...' : 'Import Services'}
        </button>

        <button 
          onClick={handleUpdateItemsJson} 
          disabled={importing || updatingJson}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: updatingJson ? '#ccc' : '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: updatingJson ? 'not-allowed' : 'pointer'
          }}
        >
          {updatingJson ? 'Updating...' : 'Update items.json'}
        </button>

        <button 
          onClick={handleExportToJSON} 
          disabled={importing}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#9C27B0',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: importing ? 'not-allowed' : 'pointer'
          }}
        >
          Export All Services to JSON
        </button>

        <button 
          onClick={loadServiceStats} 
          disabled={importing}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: importing ? 'not-allowed' : 'pointer'
          }}
        >
          Refresh Stats
        </button>
      </div>

      {importStatus && (
        <div style={{ 
          marginTop: '20px', 
          padding: '10px', 
          backgroundColor: importResult?.success === false ? '#ffebee' : '#e8f5e9',
          borderRadius: '4px' 
        }}>
          <p style={{ margin: 0, color: importResult?.success === false ? '#c62828' : '#2e7d32' }}>
            {importStatus}
          </p>
        </div>
      )}

      {importResult && importResult.success && (
        <div style={{ marginTop: '20px' }}>
          <h3>Import Summary</h3>
          <ul>
            <li>Services imported: {importResult.imported}</li>
            <li>Total services processed: {importResult.total}</li>
            <li>Duplicates skipped: {importResult.total - importResult.imported}</li>
            <li>Total services in database: {importResult.existingCount + importResult.imported}</li>
          </ul>
          {importResult.imported > 50 && (
            <p style={{ 
              marginTop: '10px', 
              padding: '10px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '4px',
              color: '#856404'
            }}>
              Note: items.json update was skipped due to large import. Use the "Update items.json" button to update it manually.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminImportPage;
